import type { WorldRoomDirectory, WorldRoomEntry, WorldRoomIndex } from "@cleo/contracts";

/** A rendered current directory may combine independently read coordinates.
 * Its base cut stays exact; each newer entry retains its own cut. It is never
 * serialized or admitted as an authoritative world-room-index-v1 snapshot. */
export type WorldRoomDisplay =
  | WorldRoomIndex
  | (WorldRoomDirectory & {
      readonly schemaVersion: "world-room-entry-display-v1";
      readonly head: null;
      readonly cut: null;
      readonly entryReadbacks: ReadonlyArray<WorldRoomEntry>;
    })
  | (Omit<WorldRoomIndex, "schemaVersion"> & {
      readonly schemaVersion: "world-room-display-v1";
      readonly entryReadbacks: ReadonlyArray<WorldRoomEntry>;
    })
  // The pages of the room index read so far: real head and cut, but not every
  // room. It is never admitted as an index; views that need every session
  // say they are still loading while this is what they hold.
  | (Omit<WorldRoomIndex, "schemaVersion"> & {
      readonly schemaVersion: "world-room-pages-display-v1";
      readonly complete: false;
      readonly entryReadbacks: ReadonlyArray<WorldRoomEntry>;
    });

/** False while the display holds only part of the World: pages still arriving,
 * or only the addressed session's own entry. True once the whole directory is
 * in (an index, possibly with newer entries laid over it). */
export function isWorldRoomDisplayComplete(display: WorldRoomDisplay): boolean {
  return (
    display.schemaVersion !== "world-room-pages-display-v1" &&
    display.schemaVersion !== "world-room-entry-display-v1"
  );
}

/** The sentence every view shows while it holds only part of the World. */
export const WORLD_ROOMS_LOADING_COPY = "Loading more rooms…";

export type WorldRoomPagesDisplay = Extract<
  WorldRoomDisplay,
  { readonly schemaVersion: "world-room-pages-display-v1" }
>;

export function displayWorldRoomPages(
  progress: Pick<WorldRoomIndex, "worldKey" | "head" | "cut" | "rootRoomId" | "rooms" | "sessions">,
): WorldRoomPagesDisplay {
  return {
    schemaVersion: "world-room-pages-display-v1",
    complete: false,
    worldKey: progress.worldKey,
    head: progress.head,
    cut: progress.cut,
    rootRoomId: progress.rootRoomId,
    rooms: progress.rooms,
    sessions: progress.sessions,
    entryReadbacks: [],
  };
}

/** Entries read on their own (the addressed session and its ancestors, a
 * session just opened) laid over the pages read so far. The display stays
 * incomplete. */
export function displayWorldRoomPagesWithEntries(
  pages: WorldRoomPagesDisplay,
  entries: ReadonlyArray<WorldRoomEntry>,
): WorldRoomPagesDisplay {
  const current = entries.filter((entry) => entry.worldKey === pages.worldKey);
  if (current.length === 0) return pages;
  const rooms = new Map(pages.rooms.map((room) => [room.roomId, room]));
  const sessions = new Map(pages.sessions.map((session) => [session.sessionId, session]));
  for (const entry of [...current].sort((a, b) => a.cut - b.cut)) {
    if (entry.cut < pages.cut && sessions.has(entry.session.sessionId)) continue;
    rooms.set(entry.room.roomId, entry.room);
    sessions.set(entry.session.sessionId, entry.session);
  }
  return {
    ...pages,
    rooms: [...rooms.values()].sort((a, b) => a.bornSeq - b.bornSeq),
    sessions: [...sessions.values()].sort((a, b) => a.bornSeq - b.bornSeq),
    entryReadbacks: current,
  };
}

export function displayWorldRoomEntries(
  index: WorldRoomIndex,
  entries: ReadonlyArray<WorldRoomEntry>,
): WorldRoomDisplay {
  const current = entries.filter(
    (entry) => entry.worldKey === index.worldKey && entry.cut > index.cut,
  );
  if (current.length === 0) return index;
  const rooms = new Map(index.rooms.map((room) => [room.roomId, room]));
  const sessions = new Map(index.sessions.map((session) => [session.sessionId, session]));
  for (const entry of [...current].sort((a, b) => a.cut - b.cut)) {
    rooms.set(entry.room.roomId, entry.room);
    sessions.set(entry.session.sessionId, entry.session);
  }
  return {
    ...index,
    schemaVersion: "world-room-display-v1",
    rooms: [...rooms.values()].sort((a, b) => a.bornSeq - b.bornSeq),
    sessions: [...sessions.values()].sort((a, b) => a.bornSeq - b.bornSeq),
    entryReadbacks: current,
  };
}

/** Selected entries are useful before the directory arrives, but are not an
 * index at any single cut. Keep that distinction visible to consumers. */
export function displaySelectedWorldEntries(
  entries: ReadonlyArray<WorldRoomEntry>,
): WorldRoomDisplay {
  const first = entries[0];
  if (!first) throw new Error("A selected entry display requires an entry");
  const sessions = new Map<string, WorldRoomEntry>();
  for (const entry of [...entries].sort((a, b) => a.cut - b.cut)) {
    if (entry.worldKey === first.worldKey) sessions.set(entry.session.sessionId, entry);
  }
  const current = [...sessions.values()].sort((a, b) => a.cut - b.cut);
  return {
    schemaVersion: "world-room-entry-display-v1",
    worldKey: first.worldKey,
    rootRoomId: first.rootRoomId,
    head: null,
    cut: null,
    rooms: [...new Map(current.map((entry) => [entry.room.roomId, entry.room])).values()],
    sessions: current.map((entry) => entry.session).sort((a, b) => a.bornSeq - b.bornSeq),
    entryReadbacks: current,
  };
}
